import Badge from "components/General/Badge";
import { v4 as uuidv4 } from "uuid";
import { useCollapsePanel } from "hooks/useCollapsePanel";
import { getBadgeText, stringFormattingToCurrency } from "js/formatting";
import { Trans, useTranslation } from "react-i18next";
import Tooltip from "components/General/Tooltip";
import CardDisclaimerBase from "components/General/CardDisclaimerBase";
import ContactUsDialog from "components/ModalDialogs/ContactUsDialog";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getPaymentHistory } from "redux/slices/getPaymentHistorySlice";
import { getLoans } from "service/policy/getLoans";
import {
  isCalcfocusPolicy,
  isDeferredAnnuityPlanCode,
  isOldAlicUlPolicy,
  isWholeLifePolicy,
} from "js/utilities";
import moment from "moment/moment";

const FinancialsCard = ({ policy, policyDetail }) => {
  const [loadDetails, setLoadDetails] = useState({
    maxLoanAmount: 0,
    loanBalance: 0,
    maxLoanAmountOldAlicUl: 0,
  });
  const [contactUsDialogState, setContactUsDialogState] = useState(false);
  const { t } = useTranslation();
  const uuid = uuidv4();
  useCollapsePanel(uuid);
  const [monthlyDeductions, setMonthlyDeductions] = useState(0);
  const dispatch = useDispatch();
  const isOldAlicUL = isOldAlicUlPolicy(policy);

  useEffect(() => {
    const fetchData = async () => {
      if (!policy?.contractID) return;

      try {
        const response = await getLoans(policy.contractID);
        const productType = policy?.product?.type?.toUpperCase();
        const maxLoanAmountOldAlicUl =
          productType === "LQ" || productType === "LZ"
            ? response?.vulLoans?.find((x) => x.type === "Fixed")?.amount ?? 0
            : response?.ulLoans?.find((x) => x.type === "Fixed")?.amount ?? 0;

        setLoadDetails({
          maxLoanAmount: response?.wlLoans?.amount,
          loanBalance: response?.allProducts?.loanBalance,
          maxLoanAmountOldAlicUl,
        });
        const paymentHistoryResponse = await dispatch(
          getPaymentHistory([
            {
              contractId: policy.contractID,
              rawResponse: true,
            },
          ])
        ).unwrap();
        const transaction = paymentHistoryResponse
          .flatMap((history) => history.transactions)
          .filter((transaction) => transaction?.itemCode === "119")
          .sort(
            (a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate)
          )[0];

        let monthlyExpense = 0;
        if (transaction) {
          const supplementalBenefitCharge =
            transaction?.supplementalBenefitsAndCharges.find(
              (item) => item?.supplementalBenefitsAndChargesCode === "140"
            );
          if (
            supplementalBenefitCharge &&
            supplementalBenefitCharge.transactionAmount
          ) {
            monthlyExpense = parseFloat(
              supplementalBenefitCharge.transactionAmount
            );
          }
        }
        setMonthlyDeductions(monthlyExpense);
      } catch (error) {
        console.error("Error", error);
      }
    };
    fetchData();
  }, [policy?.contractID]);

  return (
    <>
      <div className="ui-card">
        <div className="p-4 xl:pt-6 xl:px-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            <h2 className="mb-0">
              <button
                id={uuid}
                className="flex items-center group md:hidden"
                aria-controls={`${uuid}-panel`}
                aria-expanded="true"
                aria-label={t(
                  "AgentFinancialsCard-expand-collapsfinancial-label"
                )}
              >
                <span className="inline-block p-1 rounded-full mr-1 text-ui-blue group-hover:text-white group-hover:bg-ui-blue">
                  <svg
                    className="ui-icon ui-plus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-plus"></use>
                  </svg>
                  <svg
                    className="ui-icon ui-minus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-minus"></use>
                  </svg>
                </span>
                <span className="text-base font-body font-bold md:ui-no-badge-icon-inline">
                  {t("AgentFinancialsCard-title")}
                </span>
              </button>
              <span className="text-xl font-body font-bold hidden md:block">
                {t("AgentFinancialsCard-title")}
              </span>
            </h2>
          </div>
        </div>
        <hr className="ui-mobile-collapse" />
        <div
          id={`${uuid}-panel`}
          className="overflow-hidden h-20 p-4 md:h-auto xl:p-6"
        >
          {!isWholeLifePolicy(policy?.product?.type) && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-6">
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("AgentFinancialsCard-label-1")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {stringFormattingToCurrency(policy.faceAmount)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary flex items-center">
                    {t("AgentFinancialsCard-label-2")}
                    {isOldAlicUL && (
                      <Tooltip
                        content={t("AgentFinancialsCard-label-2-tooltip")}
                        xMargin="mx-1"
                      />
                    )}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {stringFormattingToCurrency(
                      isOldAlicUL
                        ? policy?.financials?.cashValues[0]?.deathBenefit
                        : policy.deathBenefit
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {isOldAlicUL
                      ? t("AgentFinancialsCard-ul-maxLoanAvailable")
                      : t("AgentFinancialsCard-label-3")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {isOldAlicUL
                      ? stringFormattingToCurrency(
                          loadDetails?.maxLoanAmountOldAlicUl
                        )
                      : policy?.nfOptionDescription}
                  </div>
                </div>
                {isOldAlicUL && (
                  <>
                    <div>
                      <div className="text-xs text-theme-text-tertiary flex items-center">
                        {t("AgentFinancialsCard-ul-loanBalance")}
                        <Tooltip
                          content={
                            <Trans
                              i18nKey="AgentFinancialsCard-loanBalancelabel-tooltip"
                              components={{
                                button: (
                                  <button
                                    className="ui-btn-hyperlink"
                                    onClick={() =>
                                      setContactUsDialogState(true)
                                    }
                                  />
                                ),
                              }}
                            />
                          }
                          xMargin="mx-1"
                        />
                        <ContactUsDialog
                          dialogState={contactUsDialogState}
                          setDialogState={setContactUsDialogState}
                        />
                      </div>
                      <div className="text-sm font-semibold text-theme-text-secondary">
                        {stringFormattingToCurrency(loadDetails?.loanBalance)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-theme-text-tertiary">
                        {t("AgentFinancialsCard-ul-cashValue")}
                      </div>
                      <div className="text-sm font-semibold text-theme-text-secondary">
                        {stringFormattingToCurrency(
                          policy?.financials?.cashValues[0]?.accountValue
                        )}
                      </div>
                    </div>
                    {parseFloat(policy?.surrenderCharge) !== 0 && (
                      <div>
                        <div className="text-xs text-theme-text-tertiary">
                          {t("AgentFinancialsCard-label-6")}
                        </div>
                        <div className="text-sm font-semibold text-theme-text-secondary">
                          {stringFormattingToCurrency(policy.surrenderCharge)}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-xs text-theme-text-tertiary">
                        {t("AgentFinancialsCard-ul-cashSurrenderValue")}
                      </div>
                      <div className="text-sm font-semibold text-theme-text-secondary">
                        {stringFormattingToCurrency(
                          policy?.financials?.cashValues[0]
                            ?.estimatedSurrenderValue
                        )}
                      </div>
                    </div>
                    {!isCalcfocusPolicy(policy) && (
                      <div>
                        <div className="text-xs text-theme-text-tertiary flex items-center">
                          {t("AgentFinancialsCard-ul-monthlyDeductions")}
                          <Tooltip
                            content={t(
                              "AgentFinancialsCard-monthlyDeductions-tooltip"
                            )}
                            xMargin="mx-1"
                          />
                        </div>
                        <div className="text-sm font-semibold text-theme-text-secondary">
                          {stringFormattingToCurrency(monthlyDeductions)}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {isWholeLifePolicy(policy?.product?.type) && (
            <div>
              <div className="grid grid-cols-1 p-4 gap-4 mt-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-6 xl:mt-6">
                <div>
                  <div className="text-xs text-theme-text-tertiary flex items-center">
                    {t("AgentFinancialsCard-label-2")}

                    <Tooltip
                      content={t("AgentFinancialsCard-db-tooltip")}
                      xMargin="mx-1"
                    />
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {stringFormattingToCurrency(
                      policyDetail?.financials?.cashValues[0]?.deathBenefit
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("AgentFinancialsCard-non-forfeiture-option")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {policy?.nfOptionDescription}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("AgentFinancialsCard-max-loan-amount-available")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {stringFormattingToCurrency(loadDetails?.maxLoanAmount)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary flex items-center">
                    {t("AgentFinancialsCard-loan-balance")}
                    <Tooltip
                      content={t("AgentFinancialsCard-lb-tooltip")}
                      xMargin="mx-1"
                    />
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {stringFormattingToCurrency(loadDetails?.loanBalance)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("AgentFinancialsCard-cash-value")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {stringFormattingToCurrency(
                      policyDetail?.financials?.cashValues[0]?.accountValue
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("AgentFinancialsCard-surrender-charge")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {stringFormattingToCurrency(
                      policyDetail?.financials?.cashValues[0]?.surrenderCharge
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary flex items-center">
                    {t("AgentFinancialsCard-cash-surrender-charge")}
                    <Tooltip
                      content={t("AgentFinancialsCard-cs-tooltip")}
                      xMargin="mx-1"
                    />
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {stringFormattingToCurrency(
                      policyDetail?.financials?.cashValues[0]
                        ?.estimatedSurrenderValue
                    )}
                  </div>
                </div>
              </div>
              <hr className="ui-mobile-collapse " />
              <CardDisclaimerBase
                disclaimer={
                  <>
                    {t("AgentFinancialsCard-discription", {
                      date: isDeferredAnnuityPlanCode(policy)
                        ? moment().add(-1, "days").format("MM/DD/YYYY")
                        : moment().format("MM/DD/YYYY"),
                    })}
                    <br />
                    <Trans
                      i18nKey="AgentFinancialsCard-WL-disclaimer"
                      components={{
                        div1: <div className="pt-1" />,
                      }}
                    />
                  </>
                }
              />
            </div>
          )}
        </div>
        {!isWholeLifePolicy(policy?.product?.type) && (
          <>
            <hr className="ui-mobile-collapse" />
            <CardDisclaimerBase
              disclaimer={
                <>
                  <Trans
                    i18nKey={
                      isOldAlicUL
                        ? "AgentFinancialsCard-ul-discription"
                        : "AgentFinancialsCard-term-description"
                    }
                    components={{
                      div1: <div className="pt-1" />,
                      br: <br />,
                    }}
                    values={{
                      date: moment().format("MM/DD/YYYY"),
                    }}
                  />
                </>
              }
            />
          </>
        )}
      </div>
    </>
  );
};

export default FinancialsCard;
